import React, { useRef } from 'react';
import { LucideIcon, CalendarDays, X } from 'lucide-react';
import { Calendar } from 'primereact/calendar';

interface CustomDateInputProps {
    label?: string | React.ReactNode;
    value: Date | (Date | null)[] | null;
    onChange: (val: any) => void;
    placeholder?: string;
    icon?: LucideIcon;
    selectionMode?: "single" | "multiple" | "range";
    disabled?: boolean;
    className?: string;
    onClear?: () => void;
}

export const CustomDateInput: React.FC<CustomDateInputProps> = ({
    label,
    value,
    onChange,
    placeholder = "Tarih Seçiniz...",
    icon: Icon = CalendarDays,
    selectionMode = "single",
    disabled = false,
    className = "",
    onClear
}) => {
    const calendarRef = useRef<any>(null);

    const getDisplayValue = () => {
        try {
            if (!value) return "";
            if (Array.isArray(value)) {
                if (!value[0]) return "";
                const startDate = new Date(value[0]);
                const start = startDate.toLocaleDateString('tr-TR');
                if (selectionMode === "range") {
                    const end = value[1] ? new Date(value[1]).toLocaleDateString('tr-TR') : '...';
                    return `${start} - ${end}`;
                }
                return value.map(d => d ? new Date(d).toLocaleDateString('tr-TR') : '').join(', ');
            }
            return new Date(value).toLocaleDateString('tr-TR');
        } catch (e) {
            return "Geçersiz Tarih";
        }
    };

    const displayValue = getDisplayValue();

    return (
        <div className={`flex flex-col gap-1.5 w-full ${className}`}>
            {label && (
                <label className="text-xs font-bold text-text/70 uppercase tracking-wide flex items-center gap-1.5">
                    {label}
                </label>
            )}
            <div 
                onClick={() => {
                    if (!disabled) calendarRef.current?.show();
                }}
                className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg border 
                    transition-all duration-200 relative
                    ${disabled 
                        ? 'bg-bgAlt border-borderColor/50 cursor-not-allowed opacity-70' 
                        : 'bg-inputBg border-borderColor hover:border-primary/50 cursor-pointer group hover:shadow-sm'
                    }
                `}
            >
                <Icon size={18} className={`shrink-0 ${displayValue ? 'text-primary' : 'text-text/40 group-hover:text-primary/70'} transition-colors`} />
                
                <div className="flex-1 truncate">
                    {displayValue ? (
                        <span className="font-semibold text-inputText text-[13px]">{displayValue}</span>
                    ) : (
                        <span className="text-text/40 text-[13px] font-medium">{placeholder}</span>
                    )}
                </div>
                
                {displayValue && onClear && !disabled && (
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClear();
                        }}
                        className="shrink-0 p-1 rounded-md hover:bg-bgAlt text-text/50 hover:text-danger transition-colors z-10"
                        title="Temizle"
                    >
                        <X size={14} />
                    </button>
                )}
                
                <Calendar
                    ref={calendarRef}
                    value={value as any}
                    onChange={(e) => {
                        const val = e.value;
                        if (selectionMode === "range" && Array.isArray(val)) {
                            if (val[0] && val[1]) {
                                onChange([new Date(val[0]), new Date(val[1])]);
                                setTimeout(() => calendarRef.current?.hide(), 50);
                            } else {
                                onChange(val);
                            }
                        } else {
                            onChange(val);
                        }
                    }}
                    selectionMode={selectionMode}
                    readOnlyInput
                    className="absolute opacity-0 pointer-events-none w-0 h-0"
                    touchUI
                    hideOnDateTimeSelect={selectionMode === "single"}
                />
            </div>
        </div>
    );
};
